/**
 * Pie geometry: counts in, angles and path text out. Pure, so the shapes are
 * tested without a DOM.
 *
 * MIRRORS `status_charts.layout_pie` and `pie_svg` (src/best/metamodel), the
 * code that draws the pies on the exported report page, so the dashboard and
 * the page draw the same thing: slices run clockwise from 12 o'clock in the
 * order given, edges are rounded (not sweeps) to two decimals so the slices tile
 * the circle with no gap and the last one ends at exactly 360, a zero-count
 * status has no slice, and the box, radius and path text are the same numbers.
 *
 * WHAT IS NOT MIRRORED: the page draws a filled pie with its counts beneath; the
 * dashboard cuts a hole in the same slices (PIE_HOLE) and labels them. The
 * slices themselves are the same shapes in both.
 * If one changes, change the other.
 */

import type { StatusStyle } from '../../schema'

/** A pie is drawn in a 100 x 100 box and scaled by CSS. */
export const PIE_BOX = 100
/** Leaves room for the stroke that separates the slices, so it is not clipped. */
export const PIE_RADIUS = 48
/** The hole the dashboard cuts in the middle to make a ring; the total is written in it. */
export const PIE_HOLE = 29

/** One status and how many requirements have it, in the order to draw them. */
export interface PiePart {
  status: string
  count: number
}

/** One slice. Angles are degrees clockwise from 12 o'clock. */
export interface Wedge {
  status: string
  count: number
  start: number
  end: number
  /** `end - start`, rounded as the edges are. 360 means the whole circle. */
  sweep: number
}

/** A slice as a shape: a circle for a whole pie, otherwise a path. */
export type SliceShape =
  | { kind: 'circle'; cx: number; cy: number; r: number }
  | { kind: 'path'; d: string }

const round2 = (value: number): number => Math.round(value * 100) / 100

/** The sum of the counts. */
export function pieTotal(parts: PiePart[]): number {
  return parts.reduce((sum, part) => sum + part.count, 0)
}

/** How many requirements have one status (0 when the pie has none of it). */
export function countOf(parts: PiePart[], status: string): number {
  return parts.filter((part) => part.status === status).reduce((sum, part) => sum + part.count, 0)
}

/**
 * Lay out a pie: the angle each status covers.
 *
 * Nothing at all (every count zero) gives no wedges: a pie of nothing has no
 * angle to divide. A zero-count status is not in the result.
 */
export function layoutPie(parts: PiePart[]): Wedge[] {
  const total = pieTotal(parts)
  if (total === 0) return []

  const wedges: Wedge[] = []
  let running = 0
  let edge = 0
  for (const part of parts) {
    running += part.count
    const end = round2((360 * running) / total)
    if (part.count > 0) {
      wedges.push({
        status: part.status,
        count: part.count,
        start: edge,
        end,
        sweep: round2(end - edge),
      })
    }
    edge = end
  }
  return wedges
}

/** The point on the rim at an angle (degrees clockwise from 12 o'clock). */
export function rim(angle: number): { x: number; y: number } {
  const radians = (angle * Math.PI) / 180
  const centre = PIE_BOX / 2
  return {
    x: centre + PIE_RADIUS * Math.sin(radians),
    y: centre - PIE_RADIUS * Math.cos(radians),
  }
}

/**
 * The shape of one slice.
 *
 * A single slice is a CIRCLE: an SVG arc cannot start and end at the same
 * point, so a 360 degree 'A' draws nothing.
 */
export function sliceShape(wedge: Wedge): SliceShape {
  const centre = PIE_BOX / 2
  if (wedge.sweep >= 360) return { kind: 'circle', cx: centre, cy: centre, r: PIE_RADIUS }

  const from = rim(wedge.start)
  const to = rim(wedge.end)
  const large = wedge.sweep > 180 ? 1 : 0
  const d =
    `M${centre} ${centre} L${from.x.toFixed(2)} ${from.y.toFixed(2)} ` +
    `A${PIE_RADIUS} ${PIE_RADIUS} 0 ${large} 1 ${to.x.toFixed(2)} ${to.y.toFixed(2)} Z`
  return { kind: 'path', d }
}

/** '1 requirement', '0 requirements', '7 requirements'. */
export function requirements(count: number): string {
  return `${count} ${count === 1 ? 'requirement' : 'requirements'}`
}

/** How a status is written: upper case, except '(not stated)'. */
export function statusWord(status: string): string {
  return status === '(not stated)' ? status : status.toUpperCase()
}

/** A pie in words, for its aria-label: the total, the TBD and TBR counts, and the slices. */
export function pieDescription(label: string, parts: PiePart[]): string {
  let unresolved = `${countOf(parts, 'tbd')} TBD, ${countOf(parts, 'tbr')} TBR`
  const tbc = countOf(parts, 'tbc')
  if (tbc > 0) unresolved += `, ${tbc} TBC`
  const slices = parts
    .filter((part) => part.count > 0)
    .map((part) => `${statusWord(part.status)} ${part.count}`)
    .join(', ')
  return `${label}: ${requirements(pieTotal(parts))}, ${unresolved}. Slices: ${slices}.`
}

/** The style for a status; the "any other status" item (status null) when it has none. */
export function styleFor(styles: StatusStyle[], status: string): StatusStyle | undefined {
  return (
    styles.find((style) => style.status === status) ??
    styles.find((style) => style.status === null)
  )
}
